import { useEffect, useState } from "react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { Plus, Sparkles, X } from "lucide-react"
import { ModalShell } from "@/components/portal/ModalShell"
import {
  KNOPF_MAX, MAX_ANTWORTEN, antwortenFehler, frageAendern, frageAnlegen, vorschlagLaden,
  type FrageEntwurf, type InhaberFrage,
} from "@/lib/inhaberFragen"

/**
 * „Inhaber fragen“ (#271): Frage + bis zu drei Antwort-Knöpfe, beides als
 * KI-Vorschlag und frei änderbar. Rechts die WhatsApp-Vorschau. Gespeichert
 * wird in die Warteschlange; raus geht es am nächsten Werktag um 07:00.
 */
export function InhaberFrageDialog({
  aufgabe, bestehend, frei, inhaber, onClose,
}: {
  aufgabe: { id: number; title: string; vorgang?: string } | null
  /** Freie Frage ohne Aufgabe: der Betrieb (Paul 03.11.2026) */
  frei?: string | null
  /** Bearbeiten einer Frage aus der Warteschlange */
  bestehend?: InhaberFrage | null
  inhaber: string | null
  onClose: () => void
}) {
  const qc = useQueryClient()
  const offen = !!aufgabe || !!bestehend || !!frei
  const [thema, setThema] = useState("")
  const [e, setE] = useState<FrageEntwurf>({ frage: "", antworten: [], kontext: "" })
  const [ki, setKi] = useState(false)
  const [laedt, setLaedt] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)

  const vorschlag = async (taskId: number) => {
    setLaedt(true); setFehler(null)
    try {
      const v = await vorschlagLaden(taskId)
      setE({ frage: v.frage, antworten: v.antworten.slice(0, MAX_ANTWORTEN), kontext: v.kontext || "" })
      setKi(!!v.ki)
    } catch (err) {
      setFehler(`Kein Vorschlag: ${(err as Error).message}`)
    } finally {
      setLaedt(false)
    }
  }

  useEffect(() => {
    if (bestehend) {
      setE({ frage: bestehend.frage, antworten: [...bestehend.antworten], kontext: bestehend.kontext })
      setKi(false); setFehler(null)
    } else if (aufgabe) {
      setE({ frage: "", antworten: [], kontext: "" })
      void vorschlag(aufgabe.id)
    } else if (frei) {
      // Freie Frage: kein Vorschlag — nur die üblichen Knöpfe als Start, alles änderbar
      setE({ frage: "", antworten: ["Ja", "Nein", "Ruf mich an"], kontext: "" })
      setThema(""); setKi(false); setFehler(null)
    }
  }, [aufgabe?.id, bestehend?.id, frei])

  const speichern = useMutation({
    mutationFn: () => {
      const sauber = { ...e, frage: e.frage.trim(), antworten: e.antworten.map((a) => a.trim()).filter(Boolean), kontext: e.kontext.trim() }
      if (bestehend) return frageAendern(bestehend.id, sauber)
      return aufgabe ? frageAnlegen({ taskId: aufgabe.id }, sauber) : frageAnlegen({ tenant: frei!, thema: thema.trim() }, sauber)
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["inhaber-fragen"] }); onClose() },
    onError: (err) => setFehler((err as Error).message),
  })

  const knopfFehler = antwortenFehler(e.antworten)
  const gesperrt = !e.frage.trim() || !!knopfFehler || speichern.isPending || laedt
  const setAntwort = (i: number, wert: string) => setE((x) => ({ ...x, antworten: x.antworten.map((a, j) => (j === i ? wert : a)) }))
  const vorgang = bestehend?.vorgang || aufgabe?.vorgang || (frei ? thema.trim() : "")

  return (
    <ModalShell
      open={offen}
      onClose={onClose}
      breite={880}
      titel={aufgabe ? "Aufgabe per WhatsApp an Inhaber fragen" : "Frage an Inhaber stellen per WhatsApp"}
      untertitel={frei && !bestehend
        ? <span>Frage ohne Aufgabe. Die Antwort steht danach hier in der Spalte (kein HERO-Logbuch, weil kein Vorgang).</span>
        : <span>{vorgang ? `${vorgang} · ` : ""}{aufgabe?.title || ""}</span>}
      fuss={
        <div className="ifd-fuss">
          <span className="ifd-info">{fehler || knopfFehler || (laedt ? "Vorschlag wird geholt …" : "Geht werktags um 08:00 raus — oder sofort über „Jetzt senden“.")}</span>
          <button className="pp-btn pp-btn-sm" onClick={onClose}>Abbrechen</button>
          <button className="pp-btn pp-btn-primary pp-btn-sm" disabled={gesperrt} onClick={() => speichern.mutate()}>
            {bestehend ? "Änderung speichern" : "In die Warteschlange"}
          </button>
        </div>
      }
    >
      <div className="ifd">
        <div className="ifd-form">
          {frei && !bestehend && (
            <label className="ifd-feld">
              <span className="ifd-lbl">Thema <span className="ifd-dezent">optional, steht fett über der Frage, z. B. „Büro“ oder „Urlaub“</span></span>
              <input value={thema} maxLength={60} aria-label="Thema" onChange={(ev) => setThema(ev.target.value)} />
            </label>
          )}
          <label className="ifd-feld">
            <span className="ifd-lbl">
              Frage {ki && <span className="ifd-ki"><Sparkles className="w-3 h-3" />Vorschlag · frei änderbar</span>}
              {aufgabe && !bestehend && (
                <button type="button" className="ifd-neu" onClick={() => vorschlag(aufgabe.id)} disabled={laedt}>
                  <Sparkles className="w-3 h-3" />Neuer Vorschlag
                </button>
              )}
            </span>
            <textarea value={e.frage} maxLength={400} rows={3} aria-label="Frage"
              onChange={(ev) => setE((x) => ({ ...x, frage: ev.target.value }))} />
          </label>
          <div className="ifd-feld">
            <span className="ifd-lbl">Antwort-Knöpfe <span className="ifd-dezent">bis zu {MAX_ANTWORTEN}, je {KNOPF_MAX} Zeichen</span></span>
            {e.antworten.map((a, i) => (
              <div key={i} className="ifd-antwort">
                <input value={a} maxLength={KNOPF_MAX} aria-label={`Antwort ${i + 1}`}
                  onChange={(ev) => setAntwort(i, ev.target.value)} />
                <span className={`ifd-zaehler${a.length >= KNOPF_MAX ? " voll" : ""}`}>{a.length}/{KNOPF_MAX}</span>
                <button type="button" className="ag-knopf" aria-label={`Antwort ${i + 1} entfernen`}
                  onClick={() => setE((x) => ({ ...x, antworten: x.antworten.filter((_, j) => j !== i) }))}>
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
            {e.antworten.length === 0 && <span className="ifd-dezent">Keine Knöpfe: Antwort kommt als Text oder Sprachnachricht.</span>}
            {e.antworten.length < MAX_ANTWORTEN && (
              <button type="button" className="pp-btn pp-btn-sm" style={{ alignSelf: "flex-start" }}
                onClick={() => setE((x) => ({ ...x, antworten: [...x.antworten, ""] }))}>
                <Plus className="w-3.5 h-3.5" /> Antwort hinzufügen
              </button>
            )}
          </div>
          <label className="ifd-feld">
            <span className="ifd-lbl">Kurzer Hintergrund <span className="ifd-dezent">optional, steht klein unter der Frage</span></span>
            <input value={e.kontext} maxLength={200} aria-label="Hintergrund"
              onChange={(ev) => setE((x) => ({ ...x, kontext: ev.target.value }))} />
          </label>
          <p className="ifd-hinweis">
            Der Inhaber kann auch frei schreiben oder eine Sprachnachricht schicken. Die Antwort steht danach wörtlich im
            HERO-Logbuch des Vorgangs. Eine Aufgabe entsteht daraus nur, wenn ihr sie anlegt.
          </p>
        </div>
        <div className="ifd-handy" aria-label="Vorschau in WhatsApp">
          <span className="ifd-lbl">So sieht es {inhaber || "der Inhaber"}</span>
          <div className="ifd-chat">
            <div className="ifd-blase">
              {vorgang && <b>{vorgang}</b>}
              <span>{e.frage || "…"}</span>
              {e.kontext.trim() && <i>{e.kontext}</i>}
            </div>
            {e.antworten.filter((a) => a.trim()).map((a, i) => <div key={i} className="ifd-wa-knopf">{a}</div>)}
          </div>
        </div>
      </div>
    </ModalShell>
  )
}
